package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.PayrollRecordDto
import se.mittpass.shared.api.PayrollSummaryDto
import se.mittpass.shared.api.UpdatePayrollRecordRequest

private val payrollScope = MainScope()

private enum class PayrollViewMode { VECKA, MANAD, AR }

@Composable
fun PayrollPage(workplaceId: Long) {
    var viewMode by remember { mutableStateOf(PayrollViewMode.MANAD) }
    var anchor by remember { mutableStateOf(todayLocalDate()) }
    var records by remember { mutableStateOf<List<PayrollRecordDto>>(emptyList()) }
    var summaries by remember { mutableStateOf<List<PayrollSummaryDto>>(emptyList()) }
    var loading by remember { mutableStateOf(false) }
    var editingRecord by remember { mutableStateOf<PayrollRecordDto?>(null) }
    var drag by remember { mutableStateOf<BarDragState?>(null) }
    var windowWidth by remember { mutableStateOf(kotlinx.browser.window.innerWidth) }
    val isMobile = windowWidth < 768
    val isNarrow = windowWidth < 1024

    DisposableEffect(Unit) {
        val handler: (dynamic) -> Unit = { windowWidth = kotlinx.browser.window.innerWidth }
        kotlinx.browser.window.addEventListener("resize", handler)
        onDispose { kotlinx.browser.window.removeEventListener("resize", handler) }
    }

    val rangePair = remember(viewMode, anchor) {
        when (viewMode) {
            PayrollViewMode.VECKA -> mondayOf(anchor).let { it to addDays(it, 6) }
            PayrollViewMode.MANAD -> firstOfMonth(anchor) to addDays(firstOfMonth(addMonths(anchor, 1)), -1)
            PayrollViewMode.AR    -> "${anchor.take(4)}-01-01" to "${anchor.take(4)}-12-31"
        }
    }
    val rangeFrom = rangePair.first
    val rangeTo = rangePair.second

    fun loadRecords(silent: Boolean = false) {
        if (!silent) loading = true
        payrollScope.launch {
            payrollRepo.getRecords(workplaceId, rangeFrom, rangeTo).onSuccess { records = it }
            if (viewMode == PayrollViewMode.MANAD) {
                payrollRepo.getSummary(workplaceId, rangeFrom, rangeTo).onSuccess { summaries = it }
            }
            if (!silent) loading = false
        }
    }

    LaunchedEffect(workplaceId, viewMode, anchor) { loadRecords() }

    fun centerLabel() = when (viewMode) {
        PayrollViewMode.VECKA -> {
            val ws = mondayOf(anchor)
            val we = addDays(ws, 6)
            "v.${weekNumber(ws)} · ${shortDate(ws)} – ${shortDate(we)}"
        }
        PayrollViewMode.MANAD -> {
            val parts = anchor.split("-")
            "${MONTH_NAMES[parts[1].toInt()-1].replaceFirstChar { it.uppercase() }} ${parts[0]}"
        }
        PayrollViewMode.AR -> anchor.take(4)
    }

    fun stepAnchor(forward: Boolean) {
        val sign = if (forward) 1 else -1
        anchor = when (viewMode) {
            PayrollViewMode.VECKA -> addDays(mondayOf(anchor), 7 * sign)
            PayrollViewMode.MANAD -> addMonths(anchor, sign)
            PayrollViewMode.AR    -> addYears(anchor, sign)
        }
    }

    val totalHours = records.sumOf { it.netMinutes } / 60.0
    val approvedCount = records.count { it.approved }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.75rem") } }) {
        // Toolbar card
        Div({ style {
            property("background", "var(--rp-surface)"); property("border-radius", "16px"); property("padding", "0.75rem 1.25rem")
            property("border", "1px solid var(--rp-hl-med)")
        } }) {
            Div({ style {
                property("display", "flex"); property("align-items", "center")
                property("gap", if (isMobile) "0.5rem" else "0.75rem"); property("flex-wrap", "wrap")
            } }) {
                // Left nav
                Div({ style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                    property("background", "var(--rp-surface)"); property("border", "1px solid var(--rp-overlay)")
                    property("border-radius", "12px"); property("padding", "0.25rem")
                } }) {
                    ToolbarIconBtn("fa-solid fa-chevron-left", "Föregående") { stepAnchor(false) }
                    ToolbarTextBtn("Idag") { anchor = todayLocalDate() }
                    ToolbarIconBtn("fa-solid fa-chevron-right", "Nästa") { stepAnchor(true) }
                }

                // Center label
                Span({ style {
                    property("font-size", if (isMobile) "0.95rem" else "1.1rem")
                    property("font-weight", "700"); property("color", "var(--rp-text)")
                    property("flex", if (isMobile) "1 1 100%" else "1")
                    property("text-align", if (isMobile) "left" else "center")
                    property("padding", if (isMobile) "0.25rem 0.25rem 0" else "0 0.5rem")
                } }) { Text(centerLabel()) }

                // Right: view segmented + summary badges
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("flex-wrap", "wrap") } }) {
                    PayrollViewSegmented(viewMode, isMobile) { viewMode = it }
                    Span({ style {
                        property("background", "var(--rp-overlay)"); property("color", "var(--rp-iris)")
                        property("padding", "0.35rem 0.85rem"); property("border-radius", "50px")
                        property("font-weight", "700"); property("font-size", "0.8rem")
                    } }) { Text("${((totalHours * 10).toInt() / 10.0)}h totalt") }
                    Span({ style {
                        property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-ok-text)")
                        property("padding", "0.35rem 0.85rem"); property("border-radius", "50px")
                        property("font-weight", "700"); property("font-size", "0.8rem")
                    } }) { Text("$approvedCount/${records.size} godkända") }

                    val unapproved = records.filter { !it.approved }
                    if (unapproved.isNotEmpty()) {
                        Button({
                            attr("data-testid", "btn-approve-all-payroll")
                            onClick {
                                payrollScope.launch {
                                    unapproved.forEach { rec ->
                                        payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = true))
                                    }
                                    loadRecords(silent = true)
                                }
                            }
                            style {
                                property("background", "var(--rp-pine)")
                                property("color", "#FFFFFF")
                                property("border", "none")
                                property("padding", "0.35rem 0.85rem")
                                property("border-radius", "50px")
                                property("font-weight", "700")
                                property("font-size", "0.8rem")
                                property("cursor", "pointer")
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("gap", "0.35rem")
                            }
                        }) {
                            FaIcon("fa-solid fa-check-double", size = "0.75rem", color = "var(--rp-surface)")
                            Text("Godkänn alla (${unapproved.size})")
                        }
                    }
                }
            }
        }

        // Content area
        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.75rem") } }) {
            if (loading) {
                Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "16px"); property("text-align","center"); property("padding","2rem"); property("color","var(--rp-subtle)") } }) { Text("Laddar…") }
            } else if (records.isEmpty()) {
                Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "16px"); property("text-align","center"); property("padding","2rem"); property("color","var(--rp-muted)") } }) { Text("Inga poster för vald period.") }
            } else {
                when (viewMode) {
                    PayrollViewMode.VECKA -> PayrollWeekView(
                        weekStart = mondayOf(anchor),
                        records = records,
                        drag = drag,
                        onDragStart = { drag = it },
                        onDragUpdate = { drag = it },
                        onDragEnd = { finalDrag ->
                            val rec = records.find { it.id == finalDrag.recordId }
                            if (rec != null &&
                                (finalDrag.currentStartMin != finalDrag.origStartMin ||
                                 finalDrag.currentEndMin != finalDrag.origEndMin)) {
                                payrollScope.launch {
                                    payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(
                                        startTime = minToTimeStr(finalDrag.currentStartMin),
                                        endTime = minToTimeStr(finalDrag.currentEndMin)
                                    ))
                                    loadRecords()
                                }
                            }
                            drag = null
                        },
                        onEditRecord = { editingRecord = it },
                        onReload = { loadRecords() },
                        isMobile = isMobile
                    )
                    PayrollViewMode.MANAD -> {
                    if (summaries.isNotEmpty()) {
                        PayrollSummarySection(summaries, workplaceId, rangeFrom, rangeTo, isMobile = isMobile, onReload = { loadRecords(silent = true) })
                    }
                    PayrollMonthView(
                        records = records,
                        drag = drag,
                        onDragStart = { drag = it },
                        onDragUpdate = { drag = it },
                        onDragEnd = { finalDrag ->
                            val rec = records.find { it.id == finalDrag.recordId }
                            if (rec != null &&
                                (finalDrag.currentStartMin != finalDrag.origStartMin ||
                                 finalDrag.currentEndMin != finalDrag.origEndMin)) {
                                payrollScope.launch {
                                    payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(
                                        startTime = minToTimeStr(finalDrag.currentStartMin),
                                        endTime = minToTimeStr(finalDrag.currentEndMin)
                                    ))
                                    loadRecords(silent = true)
                                }
                            }
                            drag = null
                        },
                        onEditRecord = { editingRecord = it },
                        onReload = { loadRecords(silent = true) },
                        isMobile = isMobile
                    )
                    } // end MANAD block
                    PayrollViewMode.AR -> PayrollYearView(
                        year = anchor.take(4).toInt(),
                        records = records,
                        isMobile = isMobile,
                        isNarrow = isNarrow,
                        onMonthClick = { a -> anchor = a; viewMode = PayrollViewMode.MANAD }
                    )
                }
            }
        }
    }

    // Edit modal
    editingRecord?.let { rec ->
        var editStart by remember(rec.id) { mutableStateOf(rec.startTime.take(5)) }
        var editEnd by remember(rec.id) { mutableStateOf(rec.endTime.take(5)) }
        var editBreak by remember(rec.id) { mutableStateOf(rec.breakMinutes.toString()) }
        var editNotes by remember(rec.id) { mutableStateOf(rec.notes ?: "") }

        Div({
            onClick { editingRecord = null }
            style {
                property("position","fixed"); property("inset","0"); property("z-index","1000")
                property("background","rgba(0,0,0,0.45)"); property("display","flex")
                property("align-items","center"); property("justify-content","center")
            }
        }) {
            Div({
                onClick { it.stopPropagation() }
                style {
                    property("background", "var(--rp-surface)"); property("border-radius","24px")
                    property("padding","2rem"); property("width","min(480px, 90vw)")
                }
            }) {
                H3({ style { property("margin","0 0 0.5rem 0"); property("font-size","1.2rem"); property("font-weight","800"); property("color","var(--rp-text)") } }) {
                    Text("Redigera lönepost")
                }
                Span({ style { property("color","var(--rp-subtle)"); property("font-size","0.9rem"); property("display","block"); property("margin-bottom","1.25rem") } }) {
                    Text("${listOfNotNull(rec.firstName, rec.lastName).joinToString(" ").ifEmpty { rec.userEmail }} · ${rec.day}")
                }
                // Quick Preset Pills (Schema vs Stämplat)
                val schedStart = rec.slotStartTime?.take(5)
                val schedEnd = rec.slotEndTime?.take(5)
                val ciStr = rec.checkInTime?.take(5)
                val coStr = rec.checkOutTime?.take(5)

                if ((schedStart != null && schedEnd != null) || (ciStr != null && coStr != null)) {
                    Div({ style { property("display", "flex"); property("gap", "0.5rem"); property("flex-wrap", "wrap"); property("margin-bottom", "1.25rem") } }) {
                        if (schedStart != null && schedEnd != null) {
                            val schedBreak = rec.slotBreakMinutes ?: rec.breakMinutes
                            Button({
                                onClick {
                                    editStart = schedStart
                                    editEnd = schedEnd
                                    editBreak = schedBreak.toString()
                                }
                                style {
                                    property("padding", "0.35rem 0.75rem"); property("border-radius", "8px")
                                    property("border", "1px solid var(--rp-ok-border)"); property("background", "var(--rp-ok-bg)")
                                    property("color", "var(--rp-ok-text)"); property("font-weight", "700"); property("font-size", "0.75rem")
                                    property("cursor", "pointer"); property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                                }
                            }) {
                                FaIcon("fa-regular fa-calendar", size = "0.7rem")
                                Text("Fyll pass: $schedStart–$schedEnd" + (if (schedBreak > 0) " (${schedBreak}m rast)" else ""))
                            }
                        }

                        if (ciStr != null && coStr != null) {
                            Button({
                                onClick {
                                    editStart = ciStr
                                    editEnd = coStr
                                }
                                style {
                                    property("padding", "0.35rem 0.75rem"); property("border-radius", "8px")
                                    property("border", "1px solid var(--rp-warn-border)"); property("background", "var(--rp-warn-bg)")
                                    property("color", "var(--rp-warn-text)"); property("font-weight", "700"); property("font-size", "0.75rem")
                                    property("cursor", "pointer"); property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.3rem")
                                }
                            }) {
                                FaIcon("fa-solid fa-clock", size = "0.7rem")
                                Text("Fyll stämplat: $ciStr–$coStr")
                            }
                        }
                    }
                }

                Div({ style { property("display","flex"); property("flex-direction","column"); property("gap","1.1rem") } }) {
                    Div({ style { property("display","flex"); property("gap","1rem") } }) {
                        Div({ style { property("flex","1") } }) {
                            Span({ style { payrollLabelStyle() } }) { Text("Starttid") }
                            Input(InputType.Time) { value(editStart); onInput { editStart = it.value }; style { payrollInputStyle() } }
                        }
                        Div({ style { property("flex","1") } }) {
                            Span({ style { payrollLabelStyle() } }) { Text("Sluttid") }
                            Input(InputType.Time) { value(editEnd); onInput { editEnd = it.value }; style { payrollInputStyle() } }
                        }
                    }

                    // Rast med snabbval
                    Div {
                        Span({ style { payrollLabelStyle() } }) { Text("Rast (avdrages ej lön)") }
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("margin-bottom", "0.5rem") } }) {
                            listOf(0, 15, 30, 45, 60).forEach { mins ->
                                val isSelected = editBreak == mins.toString()
                                Button({
                                    onClick { editBreak = mins.toString() }
                                    style {
                                        property("padding", "0.25rem 0.6rem"); property("border-radius", "6px")
                                        property("border", if (isSelected) "1px solid var(--rp-pine)" else "1px solid var(--rp-hl-med)")
                                        property("background", if (isSelected) "var(--rp-info-bg)" else "var(--rp-overlay)")
                                        property("color", if (isSelected) "var(--rp-pine)" else "var(--rp-subtle)")
                                        property("font-size", "0.75rem"); property("font-weight", if (isSelected) "700" else "600")
                                        property("cursor", "pointer")
                                    }
                                }) { Text("${mins}m") }
                            }
                        }
                        Input(InputType.Number) { value(editBreak); onInput { editBreak = it.value.toString() }; style { payrollInputStyle() } }
                    }

                    Div {
                        Span({ style { payrollLabelStyle() } }) { Text("Anteckning (valfritt)") }
                        Input(InputType.Text) { value(editNotes); onInput { editNotes = it.value }; style { payrollInputStyle() } }
                    }
                }
                Div({ style { property("display","flex"); property("gap","0.75rem"); property("justify-content","flex-end"); property("margin-top","1.5rem") } }) {
                    Button({
                        onClick { editingRecord = null }
                        style { property("padding","0.75rem 1.5rem"); property("border","2px solid var(--rp-pine)"); property("background","transparent"); property("color","var(--rp-pine)"); property("border-radius","50px"); property("font-weight","700"); property("cursor","pointer") }
                    }) { Text("Avbryt") }
                    Button({
                        onClick {
                            payrollScope.launch {
                                payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(
                                    startTime = editStart,
                                    endTime = editEnd,
                                    breakMinutes = editBreak.toIntOrNull(),
                                    notes = editNotes.ifBlank { null }
                                ))
                                loadRecords(silent = true)
                                editingRecord = null
                            }
                        }
                        style { property("padding","0.75rem 1.5rem"); property("border","none"); property("background","linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color","white"); property("border-radius","50px"); property("font-weight","700"); property("cursor","pointer") }
                    }) { Text("Spara") }
                }
            }
        }
    }
}

@Composable
private fun PayrollViewSegmented(current: PayrollViewMode, isMobile: Boolean, onChange: (PayrollViewMode) -> Unit) {
    val modes = listOf(PayrollViewMode.VECKA to "Vecka", PayrollViewMode.MANAD to "Månad", PayrollViewMode.AR to "År")
    Div({ style {
        property("display","inline-flex"); property("background","var(--rp-overlay)")
        property("border-radius","12px"); property("padding","3px"); property("gap","2px")
    } }) {
        modes.forEach { (mode, label) ->
            val active = mode == current
            Button({
                onClick { onChange(mode) }
                style {
                    property("background", if (active) "var(--rp-surface)" else "transparent")
                    property("color", if (active) "var(--rp-pine)" else "var(--rp-subtle)")
                    property("border","none"); property("border-radius","9px")
                    property("padding", if (isMobile) "0.45rem 0.6rem" else "0.45rem 0.85rem")
                    property("cursor","pointer"); property("font-weight", if (active) "700" else "600")
                    property("font-size","0.85rem"); property("font-family","inherit")
                    if (active) property("box-shadow","0 1px 3px rgba(13,13,24,0.08)")
                }
            }) { Text(label) }
        }
    }
}

private fun org.jetbrains.compose.web.css.StyleScope.payrollInputStyle() {
    property("padding","0.6rem 1rem"); property("border","2px solid var(--rp-hl-med)"); property("border-radius","14px")
    property("font-size","0.95rem"); property("font-weight","600"); property("outline","none")
}

private fun org.jetbrains.compose.web.css.StyleScope.payrollLabelStyle() {
    property("display","block"); property("font-weight","700"); property("font-size","0.75rem"); property("color","var(--rp-subtle)")
    property("text-transform","uppercase"); property("letter-spacing","0.5px"); property("margin-bottom","0.4rem")
}
